import { useContext, useState } from "react";

import { Condition, Procedure } from "@medplum/fhirtypes";

import { Button, Group, Paper, Text, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import { IconPlus, IconReportMedical } from "@tabler/icons-react";

import { Loading } from "@/components/Loading";
import { EncounterContext } from "@/contexts/encounter";
import { ProfileContext } from "@/contexts/profile";
import { useEncounterProcedures } from "@/encounters/contexts/EncounterProceduresContext";
import { ProceduresEditTarget } from "@/encounters/types/procedures";
import { getEncounterServiceDateKey } from "@/encounters/utils/encounter-timezone";
import { CptCopyrightNotice } from "@/shared/cpt-copyright";

import { DiagnosisRemovalModal } from "../../diagnosis-removal";
import { NoDataContainer } from "../../NoDataContainer";

import { ProceduresPicker } from "./ProceduresPicker";
import ProceduresTabConditions from "./ProceduresTabConditions";
import ProceduresTabNoData from "./ProceduresTabNoData";

interface ProceduresSectionProps {
  isEncounterActive: boolean;
  isAmendment?: boolean;
}

/**
 * The encounter's Procedures card: its diagnoses with the procedures recorded under each, and the
 * picker that stages new ones onto the encounter's next save. The Procedures tab and the Objective
 * tab both render it, and both read and stage through the same `EncounterProceduresProvider`, so
 * work added from either one shows in both.
 */
function ProceduresSection({ isEncounterActive, isAmendment = false }: ProceduresSectionProps) {
  const [isProceduresPickerOpen, { open: openProceduresPicker, close: closeProceduresPicker }] =
    useDisclosure(false);

  const { isPatient } = useContext(ProfileContext);
  const { encounter } = useContext(EncounterContext);

  // The encounter context holds the loaded data and the staged work. The section only decides what
  // to stage onto it.
  const { isLoading, data, hasLoadFailed, hasStagedChanges, stageOperation } =
    useEncounterProcedures();

  // What the picker opens on: a diagnosis to edit, or null to add to the encounter. Kept as one
  // value so the mode and the resources being edited always match.
  const [editTarget, setEditTarget] = useState<ProceduresEditTarget | null>(null);
  // The diagnosis the delete confirmation is open for, with the procedures recorded under it.
  const [deleteTarget, setDeleteTarget] = useState<ProceduresEditTarget | null>(null);

  // Only offer staging while the encounter still has a save to attach the work to. A locked
  // encounter has no Save or Sign & Lock button, so staged work would sit there unwritten and the
  // navigation blocker would keep warning about it. A client sees their own Procedures/Conditions but
  // never edits them, regardless of the encounter's state.
  const canEditProcedures = (isEncounterActive || isAmendment) && !isPatient;

  const handleNew = () => {
    setEditTarget(null);
    openProceduresPicker();
  };

  const handleEdit = (condition: Condition, procedures: Procedure[]) => {
    setEditTarget({ condition, procedures });
    openProceduresPicker();
  };

  const handleDelete = (condition: Condition, procedures: Procedure[]) => {
    setDeleteTarget({ condition, procedures });
  };

  if (isLoading) return <Loading />;
  // A failed read says so instead of falling through to the empty state and its Add: "nothing was
  // recorded on this encounter" and "we could not find out" are different things to tell a provider.
  if (hasLoadFailed)
    return (
      <Paper p={24} radius="lg">
        <Title pb="md" order={3} c="burgundy.9">
          Procedures
        </Title>
        <NoDataContainer
          hideAnchor
          linkText=""
          onAnchorClick={() => {}}
          text="The procedures could not be loaded."
          icon={<IconReportMedical size={18} color="var(--mantine-color-gray-8)" />}
        />
      </Paper>
    );

  const hasEncounterProceduresData = Boolean(
    data && (data.conditions.length || data.procedures.length),
  );

  return (
    <Paper p={24} radius="lg">
      <Group pb="md" align="center" justify="space-between">
        <Group gap="sm" align="center">
          <Title order={3} c="burgundy.9">
            Procedures
          </Title>
          {hasStagedChanges && (
            <Text fz="sm" c="gray.7" data-testid="procedures-tab-unsaved-changes">
              Unsaved — these are written when you save the encounter
            </Text>
          )}
        </Group>
        {hasEncounterProceduresData && canEditProcedures && (
          <Button
            fw={600}
            variant="outline"
            color="burgundy.9"
            onClick={handleNew}
            data-testid="procedures-tab-new-button"
            leftSection={<IconPlus size={16} color="var(--mantine-color-burgundy-9)" />}
          >
            New
          </Button>
        )}
      </Group>
      {!hasEncounterProceduresData && (
        <ProceduresTabNoData
          open={handleNew}
          isAmendment={isAmendment}
          isEncounterActive={isEncounterActive}
        />
      )}
      {hasEncounterProceduresData && data && (
        <ProceduresTabConditions
          onEdit={handleEdit}
          onDelete={handleDelete}
          conditions={data.conditions}
          procedures={data.procedures}
          canEditProcedures={canEditProcedures}
        />
      )}
      {(data?.procedures.length ?? 0) > 0 && <CptCopyrightNotice mt="lg" variant="bold" />}

      <ProceduresPicker
        onStage={stageOperation}
        opened={isProceduresPickerOpen}
        onClose={closeProceduresPicker}
        serviceDate={getEncounterServiceDateKey(encounter)}
        editTarget={editTarget && { lockedSide: "diagnosis", ...editTarget }}
      />

      <DiagnosisRemovalModal target={deleteTarget} onClose={() => setDeleteTarget(null)} />
    </Paper>
  );
}

export default ProceduresSection;
